import React, { useMemo, useState } from 'react';
import { FileTextIcon, InboxIcon, LockIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { EmptyState } from '../ui/EmptyState';
import { Field, Input } from '../ui/Field';
import { useVerification } from '../../contexts/VerificationContext';
import { METHOD_LABEL, STATUS_LABEL, formatDate } from '../../utils/verification';
import type { CaseState, VerificationCase } from '../../types/verification';
const FILTERS = ['Waiting', 'Under review', 'Needs information', 'Decided'] as const;
const STATE_TONE: Record<CaseState, 'warn' | 'brand' | 'ok' | 'neutral' | 'danger'> = {
  submitted: 'warn',
  under_review: 'brand',
  information_requested: 'brand',
  approved: 'ok',
  rejected: 'neutral',
  expired: 'neutral',
  withdrawn: 'neutral'
};

/**
 * The verification queue. Used by an institution officer (scoped to their own
 * institution) and by the Campuzon verification team for document cases.
 *
 * A reviewer sees the claim, the evidence and any flags side by side — and is
 * required to give a reason, because the person is shown it.
 */
export function CaseQueue({
  /** When set, only this institution's cases are reachable at all. */
  institutionId,
  reviewer,
  /** An institution officer may confirm affiliation; they never see other institutions. */
  mode
}: {
  institutionId: string | null;
  reviewer: string;
  mode: 'institution' | 'campuzon';
}) {
  const {
    cases,
    institutions,
    decideCase,
    setCaseState
  } = useVerification();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('Waiting');
  const scoped = useMemo(() => cases.filter(entry => {
    if (institutionId && entry.institutionId !== institutionId) return false;
    // An institution officer reviews affiliation confirmations; document
    // review belongs to the Campuzon team.
    if (mode === 'institution') return entry.method === 'institution_review';
    return entry.method === 'document';
  }), [cases, institutionId, mode]);
  const shown = scoped.filter(entry => {
    if (filter === 'Waiting') return entry.state === 'submitted';
    if (filter === 'Under review') return entry.state === 'under_review';
    if (filter === 'Needs information') return entry.state === 'information_requested';
    return entry.state === 'approved' || entry.state === 'rejected';
  });
  const excluded = cases.length - scoped.length;
  return <div className="space-y-4" data-id="element-5671" data-mp="2xilm3f3oxis6">
      <Panel title="Verification queue" data-id="element-5672" data-mp="32fluvxrxczmz">
        <ChoiceGroup label="Queue" options={FILTERS} value={filter} onChange={setFilter} data-id="element-5673" data-mp="32gn5k1513k46" />
        <div className="mt-3 space-y-2" data-id="element-5674" data-mp="36gxe9lk7gomq">
          <p className="flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-5675" data-mp="18244m304xoo9">
            <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-5676" data-mp="18475ji2tt27r" />
            {mode === 'institution' ? `You can see verification requests for ${institutions.find(entry => entry.id === institutionId)?.name ?? 'your institution'} only. ${excluded} requests exist elsewhere on Campuzon and are not reachable from this console.` : `Document cases only. Confirming an affiliation is the institution's decision, not Campuzon's — those cases are not in this queue.`}
          </p>
          <p className="max-w-[80ch] text-body-sm text-ink-muted" data-id="element-5677" data-mp="1nhhjpys832mb">
            Nothing here gives access to a student's notes, messages, wellbeing records, financial
            records or housing details.
          </p>
        </div>
      </Panel>

      {shown.length === 0 ? <Panel data-id="element-5678" data-mp="19jt3x5d0ilo4">
          <EmptyState icon={<InboxIcon className="h-5 w-5" data-id="element-5680" data-mp="19l446v791bah" />} title="Nothing in this queue" description="Switch queue to see cases at another stage." data-id="element-5679" data-mp="19i86n7zbcomk" />
        </Panel> : <ul className="space-y-4" data-id="element-5681" data-mp="3ubbp0wkf2poa">
          {shown.map(entry => <li key={entry.id} data-id="element-5682" data-mp="1lzr49jyex7he">
              <CaseCard item={entry} reviewer={reviewer} onDecide={(outcome, reason) => decideCase(entry.id, outcome, reason, reviewer)} onState={(state, detail) => setCaseState(entry.id, state, detail, reviewer)} data-id="element-5683" data-mp="1lypq36m3o4vg" />
            </li>)}
        </ul>}
    </div>;
}
function CaseCard({
  item,
  reviewer,
  onDecide,
  onState
}: {
  item: VerificationCase;
  reviewer: string;
  onDecide: (outcome: 'approved' | 'rejected', reason: string) => void;
  onState: (state: CaseState, detail: string) => void;
}) {
  const {
    institutions
  } = useVerification();
  const [reason, setReason] = useState('');
  const institution = institutions.find(entry => entry.id === item.institutionId);
  const decided = item.state === 'approved' || item.state === 'rejected';
  return <Panel title={`${item.displayName} · @${item.handle}`} description={`${item.id} · ${METHOD_LABEL[item.method]} · opened ${formatDate(item.submittedAt)}`} action={<div className="flex flex-wrap items-center gap-2" data-id="element-5685" data-mp="csfnt8zxqx0m">
          {item.priority === 'high' && <Badge tone="danger" data-id="element-5686" data-mp="qrytlv59xw9r">High priority</Badge>}
          <Badge tone={STATE_TONE[item.state]} data-id="element-5687" data-mp="qpcp64dojwvr">{item.state.replace('_', ' ')}</Badge>
        </div>} data-id="element-5684" data-mp="pbbf3oz7t04t">
      <div className="grid gap-5 lg:grid-cols-2" data-id="element-5688" data-mp="lqjbaauhe3v2">
        <div data-id="element-5689" data-mp="3u97aabo9mq2w">
          <h3 className="text-body font-semibold text-ink" data-id="element-5690" data-mp="3u8p6mtz9x9ky">What is claimed</h3>
          <dl className="mt-2 space-y-1.5 text-body-sm" data-id="element-5691" data-mp="3pa460tg40nvz">
            <Pair label="Institution" value={institution?.name ?? '—'} data-id="element-5692" data-mp="7sky38rwcdh5" />
            <Pair label="Programme" value={item.claimed.programme} data-id="element-5693" data-mp="7tw31bx2vr9u" />
            {item.claimed.level && <Pair label="Level" value={item.claimed.level} data-id="element-5694" data-mp="30a1aesgd7st" />}
            <Pair label="Status" value={STATUS_LABEL[item.claimed.status]} data-id="element-5695" data-mp="3bgwdmzk7fod8" />
          </dl>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-5696" data-mp="36j3888qq6ye0">
            All of the above was entered by the applicant. None of it is established until you decide.
          </p>
        </div>

        <div data-id="element-5697" data-mp="1fe9u5dt4daqy">
          <h3 className="text-body font-semibold text-ink" data-id="element-5698" data-mp="1fdq9hpc34898">Evidence</h3>
          {item.evidence.length === 0 ? <p className="mt-2 text-body-sm text-ink-muted" data-id="element-5699" data-mp="1k6b1qmfcedan">
              Nothing attached. This request asks you to confirm from your own records.
            </p> : <ul className="mt-2 space-y-1.5" data-id="element-5700" data-mp="rh46pxf3k5u5">
              {item.evidence.map(entry => <li key={entry.id} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-5701" data-mp="wf5mo9h87p1e">
                  <FileTextIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-5702" data-mp="10gr66do9n1kk" />
                  <span data-id="element-5703" data-mp="15g3p4nc0nohv">
                    {entry.kind} — {entry.filename}
                    <span className="block text-caption text-ink-subtle" data-id="element-5704" data-mp="15dzgxt7343bs">
                      Visible to you as the assigned reviewer. Not downloadable outside this case.
                    </span>
                  </span>
                </li>)}
            </ul>}
        </div>
      </div>

      {item.conflict && <div className="mt-4 rounded-md border-2 border-warn-200 bg-warn-50 p-4" data-id="element-5705" data-mp="2m030arwhci4e">
          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-5706" data-mp="2h94e5bot8pt8">
            <ShieldAlertIcon aria-hidden className="h-5 w-5 shrink-0 text-warn-700" data-id="element-5707" data-mp="2d8auiuiehx65" />
            Declared information differs from the institutional record
          </p>
          <p className="mt-1.5 text-body-sm text-ink" data-id="element-5708" data-mp="286v8az4hlkm6">
            {item.conflict.field} — declared as “{item.conflict.declared}”, held as “
            {item.conflict.institution}”.
          </p>
          <p className="mt-1.5 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-5709" data-mp="1ycuum8v5371u">
            Treat this as a discrepancy, not a deception. Departments rename programmes and students
            transfer. Ask before refusing.
          </p>
        </div>}

      {item.riskSignals.length > 0 && <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-5710" data-mp="3ol3g1nkllpdr">
          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-5711" data-mp="br3ffjo1nthg">
            <UsersIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-5712" data-mp="fqvgzcf0szdb" />
            Flagged for your attention
          </p>
          <ul className="mt-2 space-y-1.5" data-id="element-5713" data-mp="1yxp0edfxk4d">
            {item.riskSignals.map(signal => <li key={signal} className="text-body-sm text-ink" data-id="element-5714" data-mp="6xqqt9jd7mop">
                {signal}
              </li>)}
          </ul>
          <p className="mt-2 max-w-[74ch] text-caption text-ink-subtle" data-id="element-5715" data-mp="3t7kzu0e21oxo">
            These are observations for a human to weigh. No account is refused automatically, and the
            other account involved is not named here.
          </p>
        </div>}

      <div className="mt-4 border-t border-line pt-4" data-id="element-5716" data-mp="3k87zpybtmcpm">
        <h3 className="text-body font-semibold text-ink" data-id="element-5717" data-mp="1dgysrfkpungn">Activity</h3>
        <ol className="mt-2 space-y-2" data-id="element-5718" data-mp="1ieqlgbt6qdlw">
          {item.events.map(event => <li key={event.id} className="text-body-sm text-ink" data-id="element-5719" data-mp="1siyk513ie38p">
              <span className="text-ink-muted" data-id="element-5720" data-mp="1sj6x0szmt8jw">{formatDate(event.at)} · {event.actor} · </span>
              {event.action} — {event.detail}
            </li>)}
        </ol>
      </div>

      {decided ? <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-5721" data-mp="21dd9xxlo0w62">
          <p className="text-body font-semibold text-ink" data-id="element-5722" data-mp="1wiz9vhw260v2">
            {item.decision.outcome === 'approved' ? 'Approved' : 'Not verified'} by{' '}
            {item.decision.by}
          </p>
          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-5723" data-mp="269k5cwgfqp9r">{item.decision.reason}</p>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-5724" data-mp="2k9397d27reg9">
            This decision stays on record. An appeal opens a new case rather than replacing it.
          </p>
        </div> : <div className="mt-4 border-t border-line pt-4" data-id="element-5725" data-mp="px50rdbqamlf">
          <Field label="Reason for your decision" htmlFor={`vc-reason-${item.id}`} hint="Required. The applicant is shown this, so write it for them — not for the file." data-id="element-5726" data-mp="lui6z6vhyrll">
            <Input id={`vc-reason-${item.id}`} value={reason} onChange={event => setReason(event.target.value)} data-id="element-5727" data-mp="13235kgipoul9" />
          </Field>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-5728" data-mp="t4onkch53xqu">
            <Button variant="primary" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide('approved', reason.trim())} data-id="element-5729" data-mp="t5zsifmbnbjj">
              Confirm affiliation
            </Button>
            <Button variant="secondary" size="sm" disabled={reason.trim().length === 0} onClick={() => onState('information_requested', reason.trim())} data-id="element-5730" data-mp="1h1m026ilo7f3">
              Request more information
            </Button>
            <Button variant="danger" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide('rejected', reason.trim())} data-id="element-5731" data-mp="24s86jtcyhjmz">
              Cannot confirm
            </Button>
            {item.state === 'submitted' && <Button variant="ghost" size="sm" onClick={() => onState('under_review', `Taken by ${reviewer}.`)} data-id="element-5732" data-mp="2m1kavsc596d0">
                Take for review
              </Button>}
          </div>
          {reason.trim().length === 0 && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-5733" data-mp="300juumopox2u">
              A decision cannot be recorded without a reason the applicant can read.
            </p>}
        </div>}
    </Panel>;
}
function Pair({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex flex-wrap gap-x-2" data-id="element-5734" data-mp="3eq7znxw3qdvb">
      <dt className="text-ink-muted" data-id="element-5735" data-mp="3erj1vt9wfdkb">{label}:</dt>
      <dd className="text-ink" data-id="element-5736" data-mp="3ir9yweq0qpxa">{value}</dd>
    </div>;
}